import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';
import { map } from 'rxjs/operators';

@Component({
  selector: 'crm-hd-task-type-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent],
  templateUrl: './hd-task-type-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HdTaskTypeMultiSelectComponent implements OnInit, OnChanges {
  private static readonly ENTITY_CACHE_KIND = 'hd-task-type';

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @Input() public taskTypes: HelpdeskTaskTypeFindInterface[] = [];

  @Input() public selectedTaskTypes: HelpdeskTaskTypeFindInterface[] = [];
  @Output() public selectedTaskTypesChange = new EventEmitter<HelpdeskTaskTypeFindInterface[]>();

  @Input() public customRequest = false;
  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public selectLabel = 'Тип заявки';
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public appendTo: string;

  private catalogLoaded = false;
  private pendingTaskTypeIds: number[] | undefined;

  @Input() public set selectedTaskTypeIds(ids: number[] | undefined) {
    this.pendingTaskTypeIds = this.normalizeIds(ids);
    this.syncSelectedFromCatalog();
  }

  public constructor(
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService,
    private readonly entityCache: DropdownEntityCacheService
  ) {}

  public onSelectedTaskTypesChange(taskTypes: HelpdeskTaskTypeFindInterface[]): void {
    this.entityCache.putEntities(HdTaskTypeMultiSelectComponent.ENTITY_CACHE_KIND, taskTypes ?? []);
    this.selectedTaskTypesChange.emit(taskTypes);
  }

  public ngOnInit(): void {
    if (
      !this.customRequest &&
      this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.HELPDESK?.enabled
    ) {
      const fingerprint = this.entityCache.fingerprint({});
      this.entityCache
        .loadFirstPage(HdTaskTypeMultiSelectComponent.ENTITY_CACHE_KIND, fingerprint, () =>
          this.helpDeskTaskTypeService.find<HelpdeskTaskTypeFindInterface>().pipe(
            map((items) => {
              const list = Array.isArray(items) ? items : [];
              return { items: list, count: list.length };
            })
          )
        )
        .subscribe({
          next: ({ items }) => {
            this.taskTypes = items ?? [];
            this.catalogLoaded = true;
            this.syncSelectedFromCatalog();
            this.cdRef.markForCheck();
          }
        });
      return;
    }

    if (!this.customRequest) {
      this.catalogLoaded = true;
      this.syncSelectedFromCatalog();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.customRequest && changes.taskTypes) {
      this.catalogLoaded = true;
      this.syncSelectedFromCatalog();
    }
  }

  private syncSelectedFromCatalog(): void {
    const ids = this.pendingTaskTypeIds;
    if (!ids?.length) {
      if (this.selectedTaskTypes.length) {
        this.selectedTaskTypes = [];
        this.cdRef.markForCheck();
      }
      return;
    }

    if (!this.isCatalogReady()) {
      return;
    }

    const next = this.taskTypes.filter(({ id }) => ids.includes(+id));
    const hasStaleKeys = next.length !== ids.length;
    if (!this.hasSameSelection(next)) {
      this.selectedTaskTypes = next;
      this.entityCache.putEntities(HdTaskTypeMultiSelectComponent.ENTITY_CACHE_KIND, next);
      if (hasStaleKeys) {
        this.onSelectedTaskTypesChange(next);
      }
      this.cdRef.markForCheck();
    } else if (hasStaleKeys) {
      this.onSelectedTaskTypesChange(next);
    }
  }

  private isCatalogReady(): boolean {
    return this.catalogLoaded;
  }

  private normalizeIds(ids: number[] | undefined): number[] | undefined {
    if (!ids?.length) {
      return undefined;
    }
    return ids.map((id) => (typeof id === 'string' ? +id : id));
  }

  private hasSameSelection(next: HelpdeskTaskTypeFindInterface[]): boolean {
    if (next.length !== this.selectedTaskTypes.length) {
      return false;
    }
    const nextIds = new Set(next.map(({ id }) => +id));
    return this.selectedTaskTypes.every(({ id }) => nextIds.has(+id));
  }
}
